import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail, downloadBlobResponse } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ProdiSelect from "@/components/ProdiSelect";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Trash2, Search, FileSpreadsheet, FileText,
    CheckCircle2, Clock, ChevronLeft, ChevronRight, ClipboardList, X,
} from "lucide-react";
import {
    ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
} from "recharts";
import { chartTooltipProps, formatAngkaTooltip } from "@/lib/chartTooltip";
import { toast } from "sonner";
import { seesAllProdi, isAdminKoas, istilahProdi } from "@/lib/roles";
import BreakdownProdiOrientasi from "@/components/BreakdownProdiOrientasi";
import { formatWIB } from "@/lib/datetime";
import StickyHeader from "@/components/StickyHeader";

// Baris per halaman -- disamakan dgn pola pagination server-side di Peserta Didik
// & Rotasi Klinik (lihat pages/Peserta.jsx).
const PAGE_SIZE = 100;

const STATUS_STYLE = {
    "Orientasi": "bg-amber-100 text-amber-700 border-amber-300",
    "Orientasi Selesai": "bg-emerald-100 text-emerald-700 border-emerald-300",
};

// "Spesialis" -> prodi jenjang Sp1, "Subspesialis" -> prodi jenjang Sp2, "Koas" ->
// prodi jenjang Koas -- sama persis relasi jenjang/prodi di Peserta Didik (lihat
// backend _kategori_from_jenjang).
const KATEGORI_JENJANG = { Spesialis: "Sp1", Subspesialis: "Sp2", Koas: "Koas" };

// Warna batang grafik kategori -- 1 warna tetap per kategori (bukan ternary 2
// kategori) supaya konsisten begitu tab/urutan data berubah.
const KATEGORI_CHART_COLOR = { Spesialis: "#00B9AD", Subspesialis: "#267B84", Koas: "#F5A524" };

// Menu "Orientasi RS": memantau orientasi peserta didik baru di rumah sakit sebelum
// mulai stase, dengan alur konfirmasi selesai oleh Super Admin/Admin UFP.
//
// Read-mostly by design: entri di sini SELALU berasal dari Peserta Didik yang
// sudah terdaftar (dikirim lewat toolbar bulk action "Kirim ke Orientasi RS" di
// halaman Peserta Didik, lihat Peserta.jsx) -- tidak ada lagi input manual/Import
// Excel/Edit di sini, supaya data tidak pernah bisa tidak sinkron dengan sumbernya.
// peserta_id sudah pasti valid sejak entri dibuat (bukan dicocokkan lewat NPM saat
// konfirmasi seperti versi sebelumnya), jadi tombol Konfirmasi Selesai cukup
// membaca peserta_id yang tersimpan (lihat backend confirm_orientasi_rs).
export default function OrientasiRS() {
    const { user } = useAuth();
    const { effectiveTheme } = useTheme();
    const isDark = effectiveTheme === "dark";
    const isSuper = seesAllProdi(user.role); // lihat semua prodi
    const isKoas = isAdminKoas(user.role); // satu Modul Koas, atau lintas SEMUA prodi Koas utk akun lama tanpa modul
    const istilah = istilahProdi(user.role);
    const canConfirm = isSuper; // Konfirmasi Selesai: Super Admin & Admin UFP
    const canDelete = isSuper; // Hapus entri: Super Admin & Admin UFP -- Admin Prodi/Admin Koas tidak boleh (lihat backend delete_orientasi_rs)
    const showAksiColumn = canConfirm || canDelete; // kolom Aksi disembunyikan total kalau tak ada aksi apa pun (Admin Prodi/Admin Koas)

    const chartGridStroke = isDark ? "#3A3A3A" : "#E2E8F0";
    const chartTickFill = isDark ? "#94A3B8" : "#475569";

    const [prodiList, setProdiList] = useState([]);
    const prodiMap = useMemo(() => {
        const m = {};
        prodiList.forEach((p) => (m[p.id] = p));
        return m;
    }, [prodiList]);

    // Admin Prodi hanya bisa punya 1 kategori yg cocok dgn jenjang prodinya sendiri
    // -- tab kategori yg lain cuma akan dipentalkan backend, jadi disembunyikan
    // sama sekali (pola sama seperti RotasiKlinikViewTabs utk Admin Prodi).
    // Admin Koas tidak punya prodi_id (lintas prodi) -- jenjangnya SELALU "Koas",
    // tidak lewat prodiMap[user.prodi_id] spt Admin Prodi, jadi sudah diketahui
    // SEJAK RENDER PERTAMA (tidak perlu menunggu prodiList termuat spt Admin Prodi).
    const myJenjang = isKoas ? "Koas" : (!isSuper ? prodiMap[user.prodi_id]?.jenjang : null);
    const showKategoriTabs = isSuper || !myJenjang;
    // Admin Koas: inisialisasi LANGSUNG ke "Koas" (diketahui sinkron dari isKoas,
    // beda dgn Admin Prodi yg baru tahu jenjangnya setelah prodiList termuat) --
    // supaya tidak sempat kedip ke tab "Spesialis" dulu sebelum effect di bawah jalan.
    const [kategori, setKategori] = useState(() => (isKoas ? "Koas" : "Spesialis"));
    useEffect(() => {
        if (isKoas) return; // sudah benar sejak initial state di atas
        if (!isSuper && myJenjang) {
            setKategori(myJenjang === "Sp2" ? "Subspesialis" : "Spesialis");
        }
    }, [isSuper, myJenjang, isKoas]);

    const prodiOptions = useMemo(
        () => prodiList.filter((p) => p.jenjang === KATEGORI_JENJANG[kategori]),
        [prodiList, kategori]
    );

    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const requestIdRef = useRef(0);
    const [q, setQ] = useState("");
    const [filter, setFilter] = useState({ prodi_id: "all", status: "all" });

    const [summary, setSummary] = useState(null);

    const [confirmTarget, setConfirmTarget] = useState(null);
    const [confirmBusy, setConfirmBusy] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);

    // Bulk confirm -- pola checkbox selection sama seperti Peserta Didik (lihat
    // pages/Peserta.jsx: selectedIds Set + toggleRow/toggleAll/clearSelection).
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
    const [bulkConfirmBusy, setBulkConfirmBusy] = useState(false);
    const selectedCount = selectedIds.size;
    // Hanya entri berstatus "Orientasi" (belum selesai) yg boleh dipilih utk bulk
    // confirm -- yg sudah "Orientasi Selesai" tidak relevan dikonfirmasi lagi.
    const selectableItems = useMemo(() => items.filter((r) => r.status === "Orientasi"), [items]);
    // Nama, NPM, Prodi, No HP, Email, Status, Diubah = 7 kolom tetap + checkbox (canConfirm) + Aksi (showAksiColumn).
    const tableColSpan = 7 + (canConfirm ? 1 : 0) + (showAksiColumn ? 1 : 0);

    const loadProdi = async () => {
        try { const { data } = await api.get("/prodi"); setProdiList(data); } catch {}
    };

    const buildFilterParams = () => {
        const params = { kategori, q: q || undefined };
        if (isSuper && filter.prodi_id !== "all") params.prodi_id = filter.prodi_id;
        if (filter.status !== "all") params.status = filter.status;
        return params;
    };

    const load = async (pageArg) => {
        const targetPage = pageArg ?? page;
        const reqId = ++requestIdRef.current;
        setLoading(true);
        try {
            const params = { ...buildFilterParams(), page: targetPage, page_size: PAGE_SIZE };
            const { data } = await api.get("/orientasi-rs", { params });
            if (reqId !== requestIdRef.current) return;
            setItems(data.items);
            setTotal(data.total);
            setSelectedIds(new Set());
        } catch {
            if (reqId !== requestIdRef.current) return;
            toast.error("Gagal memuat data Orientasi RS");
        } finally {
            if (reqId === requestIdRef.current) setLoading(false);
        }
    };

    const loadSummary = async () => {
        try { const { data } = await api.get("/orientasi-rs/dashboard-summary"); setSummary(data); } catch {}
    };

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const goToPage = (p) => {
        const clamped = Math.min(Math.max(1, p), totalPages);
        if (clamped === page) return;
        setPage(clamped);
        load(clamped);
    };

    useEffect(() => { loadProdi(); loadSummary(); }, []);
    // Filter/kategori/search berubah -> selalu kembali ke halaman 1.
    useEffect(() => { setPage(1); load(1); /* eslint-disable-line */ }, [kategori, q, filter.prodi_id, filter.status]);

    const doDelete = async () => {
        if (!deleteTarget) return;
        try {
            await api.delete(`/orientasi-rs/${deleteTarget.id}`);
            toast.success("Entri dihapus");
            setDeleteTarget(null);
            load();
            loadSummary();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
    };

    const doConfirm = async () => {
        if (!confirmTarget) return;
        setConfirmBusy(true);
        try {
            const { data } = await api.post(`/orientasi-rs/${confirmTarget.id}/confirm`);
            toast.success(
                data.peserta_synced
                    ? "Orientasi selesai -- Tahap/Stase peserta ikut diperbarui otomatis."
                    : "Orientasi ditandai selesai."
            );
            setConfirmTarget(null);
            load();
            loadSummary();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setConfirmBusy(false);
        }
    };

    const toggleRow = (id) => setSelectedIds((prev) => {
        const n = new Set(prev);
        n.has(id) ? n.delete(id) : n.add(id);
        return n;
    });
    const toggleAll = () => setSelectedIds((prev) =>
        prev.size === selectableItems.length ? new Set() : new Set(selectableItems.map((i) => i.id)));
    const clearSelection = () => setSelectedIds(new Set());

    const doBulkConfirm = async () => {
        if (selectedCount === 0) return;
        setBulkConfirmBusy(true);
        try {
            const { data } = await api.post("/orientasi-rs/bulk-confirm", { ids: [...selectedIds] });
            toast.success(
                `${data.confirmed} entri dikonfirmasi selesai` +
                (data.synced > 0 ? ` (${data.synced} Tahap/Stase peserta otomatis diperbarui)` : "") + "."
            );
            if (data.failed.length > 0) {
                toast.error(`${data.failed.length} entri gagal dikonfirmasi (mis. sudah selesai sebelumnya).`);
            }
            setBulkConfirmOpen(false);
            clearSelection();
            load();
            loadSummary();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setBulkConfirmBusy(false);
        }
    };

    const download = async (fmt) => {
        try {
            const params = { kategori };
            if (isSuper && filter.prodi_id !== "all") params.prodi_id = filter.prodi_id;
            if (filter.status !== "all") params.status = filter.status;
            const res = await api.get(`/orientasi-rs/export/${fmt}`, { params, responseType: "blob" });
            downloadBlobResponse(res, `aura_orientasi-rs.${fmt === "excel" ? "xlsx" : "pdf"}`);
        } catch { toast.error("Gagal export"); }
    };

    const kategoriChartData = summary
        ? summary.kategori_distribution.map((d) => ({ ...d, fill: KATEGORI_CHART_COLOR[d.label] || "#94A3B8" }))
        : [];

    return (
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8 space-y-4">
            <StickyHeader>
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                    <div>
                        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Peserta Didik</div>
                        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Plus Jakarta Sans" }}>
                            Orientasi RS
                        </h1>
                        <p className="text-sm text-slate-500 mt-1">
                            Pantau orientasi peserta didik baru sebelum mulai stase. Entri dikirim dari menu
                            Peserta Didik (pilih peserta → "Kirim ke Orientasi RS").
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Button variant="outline" onClick={() => download("excel")} data-testid="orientasi-export-excel">
                            <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel
                        </Button>
                        <Button variant="outline" onClick={() => download("pdf")} data-testid="orientasi-export-pdf">
                            <FileText className="w-4 h-4 mr-2" /> PDF
                        </Button>
                    </div>
                </div>

                {showKategoriTabs && (
                    <Tabs value={kategori} onValueChange={setKategori}>
                        <TabsList>
                            <TabsTrigger value="Spesialis" data-testid="orientasi-tab-spesialis">Spesialis</TabsTrigger>
                            <TabsTrigger value="Subspesialis" data-testid="orientasi-tab-subspesialis">Subspesialis</TabsTrigger>
                            <TabsTrigger value="Koas" data-testid="orientasi-tab-koas">Koas</TabsTrigger>
                        </TabsList>
                    </Tabs>
                )}

                <Card className="border-slate-200 shadow-sm">
                    <CardContent className="p-4 flex flex-col md:flex-row gap-2 items-start md:items-center">
                        <div className="relative flex-1 min-w-[200px]">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <Input
                                data-testid="orientasi-search-input"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Cari nama / NPM / email..."
                                className="pl-9 h-10"
                            />
                        </div>
                        {isSuper && (
                            <ProdiSelect
                                prodi={prodiOptions}
                                value={filter.prodi_id}
                                onValueChange={(v) => setFilter({ ...filter, prodi_id: v })}
                                includeAll
                                allLabel={`Semua Prodi (${kategori})`}
                                showJenjang={false}
                                placeholder="Prodi"
                                className="w-56 h-10"
                                testId="orientasi-filter-prodi"
                            />
                        )}
                        <Select value={filter.status} onValueChange={(v) => setFilter({ ...filter, status: v })}>
                            <SelectTrigger className="w-48 h-10" data-testid="orientasi-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Semua Status</SelectItem>
                                <SelectItem value="Orientasi">Orientasi</SelectItem>
                                <SelectItem value="Orientasi Selesai">Orientasi Selesai</SelectItem>
                            </SelectContent>
                        </Select>
                    </CardContent>
                </Card>

                {canConfirm && selectedCount > 0 && (
                    <div className="rounded-lg border border-[#267B84] bg-[#267B84] text-white shadow-lg px-4 py-3 flex flex-wrap items-center gap-3" data-testid="orientasi-bulk-bar">
                        <span className="font-bold text-sm">{selectedCount} entri dipilih</span>
                        <span className="text-white">|</span>
                        <Button
                            size="sm" variant="outline" className="bg-black/10 border-white/30 text-white hover:bg-black/20 hover:text-white"
                            onClick={() => setBulkConfirmOpen(true)}
                            data-testid="orientasi-bulk-confirm-button"
                        >
                            <CheckCircle2 className="w-4 h-4 mr-2" /> Konfirmasi Selesai ({selectedCount} terpilih)
                        </Button>
                        <Button size="sm" variant="ghost" className="text-white hover:bg-black/10 ml-auto" onClick={clearSelection} data-testid="orientasi-bulk-cancel">
                            <X className="w-4 h-4 mr-1" /> Batal
                        </Button>
                    </div>
                )}
            </StickyHeader>

            {summary && (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Card className="border-slate-200 shadow-sm">
                            <CardContent className="p-4 flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-[#267B84] text-white flex items-center justify-center shrink-0">
                                    <ClipboardList className="w-5 h-5" />
                                </div>
                                <div>
                                    <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Total Orientasi</div>
                                    <div className="text-2xl font-extrabold text-slate-900">{summary.total}</div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card className="border-slate-200 shadow-sm">
                            <CardContent className="p-4 flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-emerald-600 text-white flex items-center justify-center shrink-0">
                                    <CheckCircle2 className="w-5 h-5" />
                                </div>
                                <div>
                                    <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Sudah Selesai</div>
                                    <div className="text-2xl font-extrabold text-emerald-700">{summary.selesai}</div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card className="border-slate-200 shadow-sm">
                            <CardContent className="p-4 flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-amber-500 text-white flex items-center justify-center shrink-0">
                                    <Clock className="w-5 h-5" />
                                </div>
                                <div>
                                    <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Masih Berlangsung</div>
                                    <div className="text-2xl font-extrabold text-amber-700">{summary.berlangsung}</div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
                        <Card className="border-slate-200 shadow-sm">
                            <CardContent className="p-6">
                                <h3 className="font-bold text-slate-800 mb-4">Distribusi per Kategori</h3>
                                <div className="h-56">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={kategoriChartData} margin={{ left: 0, right: 10 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke={chartGridStroke} />
                                            <XAxis dataKey="label" tick={{ fontSize: 12, fill: chartTickFill }} />
                                            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: chartTickFill }} />
                                            <Tooltip {...chartTooltipProps} formatter={formatAngkaTooltip} />
                                            <Bar dataKey="count" name="Jumlah" radius={[4, 4, 0, 0]}>
                                                {kategoriChartData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </CardContent>
                        </Card>

                        <BreakdownProdiOrientasi
                            rows={summary.per_prodi}
                            jumlah={summary.jumlah}
                            judulKolom={istilah.panjang}
                        />
                    </div>
                </>
            )}

            <Card className="border-slate-200 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full compact">
                        <thead className="bg-slate-100">
                            <tr className="text-left text-xs uppercase tracking-wider text-slate-600 font-semibold">
                                {canConfirm && (
                                    <th className="w-10 pl-4">
                                        <Checkbox
                                            checked={selectableItems.length > 0 && selectedCount === selectableItems.length
                                                ? true : selectedCount > 0 ? "indeterminate" : false}
                                            onCheckedChange={toggleAll}
                                            disabled={selectableItems.length === 0}
                                            aria-label="Pilih semua"
                                            data-testid="orientasi-select-all"
                                        />
                                    </th>
                                )}
                                <th>Nama</th><th>NPM</th><th>{istilah.singkat}</th><th>No HP</th><th>Email</th>
                                <th>Status</th><th>Diubah</th>
                                {showAksiColumn && <th className="text-right pr-4">Aksi</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={tableColSpan} className="text-center py-10 text-slate-400">Memuat...</td></tr>
                            ) : items.length === 0 ? (
                                <tr><td colSpan={tableColSpan} className="text-center py-10 text-slate-400">
                                    Belum ada data. Kirim peserta dari menu Peserta Didik (pilih peserta → "Kirim ke Orientasi RS") untuk memulai.
                                </td></tr>
                            ) : items.map((r) => (
                                <tr key={r.id} className={`border-t border-slate-200 hover:bg-slate-50 ${selectedIds.has(r.id) ? "bg-sky-50" : ""}`}>
                                    {canConfirm && (
                                        <td className="pl-4">
                                            <Checkbox
                                                checked={selectedIds.has(r.id)}
                                                onCheckedChange={() => toggleRow(r.id)}
                                                disabled={r.status !== "Orientasi"}
                                                aria-label={`Pilih ${r.nama}`}
                                                data-testid={`orientasi-select-${r.id}`}
                                            />
                                        </td>
                                    )}
                                    <td className="font-semibold text-slate-800">{r.nama}</td>
                                    <td className="font-mono text-xs">{r.npm}</td>
                                    <td className="text-slate-600">{prodiMap[r.prodi_id]?.nama_prodi || "-"}</td>
                                    <td className="text-slate-600">{r.no_hp || "-"}</td>
                                    <td className="text-slate-600">{r.email}</td>
                                    <td>
                                        <span className={`inline-block px-2 py-0.5 rounded-full border text-[11px] font-semibold ${STATUS_STYLE[r.status] || ""}`}>{r.status}</span>
                                    </td>
                                    <td className="text-[11px] text-slate-500">
                                        <div>{r.updated_by || "-"}</div>
                                        <div className="text-slate-400">{formatWIB(r.updated_at)}</div>
                                    </td>
                                    {showAksiColumn && (
                                        <td className="text-right pr-2 whitespace-nowrap">
                                            {canConfirm && r.status === "Orientasi" && (
                                                <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 mr-1" onClick={() => setConfirmTarget(r)} data-testid={`orientasi-confirm-${r.id}`}>
                                                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Konfirmasi Selesai
                                                </Button>
                                            )}
                                            {canDelete && (
                                                <Button size="sm" variant="ghost" className="text-rose-600 hover:text-rose-700" onClick={() => setDeleteTarget(r)} data-testid={`orientasi-delete-${r.id}`}>
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            )}
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="px-4 py-2 border-t bg-slate-50 text-xs text-slate-500 flex flex-wrap items-center justify-between gap-2">
                    <span>
                        Menampilkan <span className="font-bold text-slate-800">{items.length}</span> dari{" "}
                        <span className="font-bold text-slate-800">{total}</span> entri Orientasi RS
                    </span>
                    <div className="flex items-center gap-2" data-testid="orientasi-pagination">
                        <Button size="sm" variant="outline" className="h-7 px-2" disabled={page <= 1 || loading} onClick={() => goToPage(page - 1)} data-testid="orientasi-page-prev">
                            <ChevronLeft className="w-3.5 h-3.5" />
                        </Button>
                        <span className="text-slate-600">Halaman {page} dari {totalPages}</span>
                        <Button size="sm" variant="outline" className="h-7 px-2" disabled={page >= totalPages || loading} onClick={() => goToPage(page + 1)} data-testid="orientasi-page-next">
                            <ChevronRight className="w-3.5 h-3.5" />
                        </Button>
                    </div>
                </div>
            </Card>

            {/* Konfirmasi Selesai */}
            <AlertDialog open={!!confirmTarget} onOpenChange={(o) => !o && setConfirmTarget(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Konfirmasi orientasi selesai?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Status <span className="font-bold">{confirmTarget?.nama}</span> ({confirmTarget?.npm}) akan berubah
                            jadi "Orientasi Selesai", dan Tahap/Stase peserta ini di Peserta Didik ikut diperbarui otomatis
                            (kalau masih berstatus MDU/MDK).
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={confirmBusy}>Batal</AlertDialogCancel>
                        <AlertDialogAction data-testid="orientasi-confirm-submit" disabled={confirmBusy} className="bg-emerald-600 hover:bg-emerald-700" onClick={doConfirm}>
                            {confirmBusy ? "Memproses..." : "Ya, Tandai Selesai"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Konfirmasi bulk */}
            <AlertDialog open={bulkConfirmOpen} onOpenChange={(o) => !bulkConfirmBusy && setBulkConfirmOpen(o)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Konfirmasi {selectedCount} entri sekaligus?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Status <span className="font-bold">{selectedCount} entri</span> yang dipilih akan berubah jadi
                            "Orientasi Selesai", dan Tahap/Stase peserta terkait ikut diperbarui otomatis (kalau masih
                            berstatus MDU/MDK). Entri yang ternyata sudah selesai sebelumnya akan dilewati (dilaporkan,
                            bukan menggagalkan yang lain).
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={bulkConfirmBusy}>Batal</AlertDialogCancel>
                        <AlertDialogAction data-testid="orientasi-bulk-confirm-submit" disabled={bulkConfirmBusy} className="bg-emerald-600 hover:bg-emerald-700" onClick={doBulkConfirm}>
                            {bulkConfirmBusy ? "Memproses..." : `Ya, Konfirmasi ${selectedCount} Entri`}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Hapus entri Orientasi RS?</AlertDialogTitle>
                        <AlertDialogDescription>
                            {deleteTarget?.nama} (NPM: {deleteTarget?.npm}) akan dihapus permanen. Data Peserta Didik
                            yang bersangkutan TIDAK ikut terhapus/berubah.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Batal</AlertDialogCancel>
                        <AlertDialogAction data-testid="orientasi-delete-confirm" className="bg-rose-600 hover:bg-rose-700" onClick={doDelete}>Hapus</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
